import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { FieldInterventionEntityBloc } from '@connect-services/client/blocs/field-intervention-entity.bloc';
import { FieldInterventionFormListBloc } from '@connect-services/client/blocs/field-intervention-form-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { AbstractLayerProjectComponent } from '@connect-services/client/components/abstract-layer-project/abstract-layer-project.component';
import { FieldInterventionFormCategoryComponent } from '@connect-services/client/components/field-intervention-form-category/field-intervention-form-category.component';
import { MainTitleComponent } from '@connect-services/client/components/main-title/main-title.component';
import { MapboxStyleWithZoom, MapFieldInterventionSource } from '@connect-services/client/components/map/map.component';
import { ActionsEnum } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { FieldInterventionService } from '@connect-services/client/services/field-intervention.service';
import { FieldInterventionMapModeService } from '@connect-services/client/services/field-intervention-map-mode.service';
import { FieldInterventionMapRendererService } from '@connect-services/client/services/field-intervention-map-renderer.service';
import { MapFieldInterventionService } from '@connect-services/client/services/map-field-intervention.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { FieldInterventionForm } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FieldInterventionFormCategoryComponent,
        LoadingComponent,
        MainTitleComponent,
        RessourcesModule,
        SelectComponent,
    ],
    selector: 'app-field-intervention-form',
    styleUrl: './field-intervention-form-answer.component.scss',
    templateUrl: './field-intervention-form-answer.component.html',
})
export class FieldInterventionFormAnswerComponent extends AbstractLayerProjectComponent {
    public readonly fieldInterventionForms = signal<FieldInterventionForm[]>([]);
    public readonly formOptions = signal<SelectOption<string>[]>([]);
    public readonly selectedForm = signal<FieldInterventionForm | null>(null);

    protected readonly isLoading = signal<boolean>(true);
    protected readonly isNotApplicableForm = computed<boolean>(() =>
        this.fieldInterventionService.isNonApplicableForm(
            this.fieldInterventionEntityBloc.value()!,
            this.selectedForm()!,
        ),
    );

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ActionsEnum = ActionsEnum;
    protected readonly fieldInterventionEntityBloc = inject(FieldInterventionEntityBloc);
    protected readonly fieldInterventionFormListBloc = inject(FieldInterventionFormListBloc);
    protected readonly fieldInterventionService = inject(FieldInterventionService);

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly fieldInterventionBloc = inject(FieldInterventionBloc);
    private readonly fieldInterventionMapModeService = inject(FieldInterventionMapModeService);
    private readonly fieldInterventionMapRendererService = inject(FieldInterventionMapRendererService);
    private readonly mapFieldInterventionService = inject(MapFieldInterventionService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly snackBarService = inject(SnackBarService);
    private readonly sourcesService = inject(SourcesService);

    public constructor() {
        super();

        this.fieldInterventionMapModeService.enterFieldInterventionEntitiesMode();

        this.destroyRef.onDestroy(() => {
            this.mapFieldInterventionService.resetFilter();
        });

        effect(async () => {
            if (!this.fieldInterventionEntityBloc.value() || !this.fieldInterventionFormListBloc.value()) {
                return;
            }

            this.fieldInterventionForms.set(await this.filterFieldInterventionForms());

            this.formOptions.set(
                this.fieldInterventionForms().map((form: FieldInterventionForm) => ({
                    label: form.name,
                    value: form.name,
                })),
            );

            if (this.fieldInterventionForms().length) {
                this.selectedForm.set(this.fieldInterventionForms()[0]);
            }
        });

        effect(async () => {
            const layerId = this.fieldInterventionEntityBloc.value()!.layer;

            if (!layerId || !this.fieldInterventionBloc.value()) {
                return;
            }

            const layerStyle: MapboxStyleWithZoom = await this.sourcesService.getLayerStyle(
                this.fieldInterventionBloc.value()!.layerStyle,
            );

            const fieldInterventionSource = new MapFieldInterventionSource(
                layerId,
                this.fieldInterventionBloc.value()!,
                layerStyle,
                layerId,
            );
            fieldInterventionSource.isVisible = true;

            await this.fieldInterventionMapRendererService.drawSourcesWithAreaMask([fieldInterventionSource]);

            await this.mapFieldInterventionService.filterFieldInterventionEntitiesOnMap(
                [fieldInterventionSource.sourceId],
                [fieldInterventionSource],
                { entityIds: [this.fieldInterventionEntityBloc.value()!.id] },
            );
        });
    }

    public changeForm(formName: string[]): void {
        const form: FieldInterventionForm | undefined = this.fieldInterventionForms().find(
            (form: FieldInterventionForm) => form.name === formName[0],
        );

        if (!form) {
            return;
        }

        this.selectedForm.set(form);
    }

    public async goBack(): Promise<void> {
        const path = [
            RoutePathEnum.PROJECTS,
            this.projectBloc.value()?.id,
            RoutePathEnum.FIELD_INTERVENTIONS,
            this.fieldInterventionBloc.value()?.id,
            RoutePathEnum.FIELD_INTERVENTION_ENTITIES,
        ];

        await this.router.navigate(
            [
                ...path,
                {
                    outlets: { detailPanel: this.fieldInterventionEntityBloc.value()?.id },
                },
            ],
            {
                queryParams: { ...this.activatedRoute.snapshot.queryParams },
                queryParamsHandling: 'merge',
            },
        );

        this.fieldInterventionService.isEntityResultDisplayed.set(false);
    }

    private async filterFieldInterventionForms(): Promise<FieldInterventionForm[]> {
        try {
            const allFieldInterventionForms: FieldInterventionForm[] =
                await this.fieldInterventionService.fetchAllForms();

            return allFieldInterventionForms.filter((form: FieldInterventionForm) =>
                this.fieldInterventionEntityBloc.value()!.formIds!.includes(form.id),
            );
        } catch {
            this.snackBarService.openErrorSnackBar('errorGET');

            return [];
        } finally {
            this.isLoading.set(false);
        }
    }
}
